<template>
  <div class="carteira-cadastro-campos-vue carteira-cadastros-vue mt-3">
    <div class="carteira-cadastros-vue__bootstrap-modals">
      <Modal
        modal-id="modalCarteiraCadastroCamposErro"
        :title="errorModalTitle"
        dialog-class="modal-dialog-centered"
      >
        <template #body>
          <p v-if="errorModalMessage" class="mb-2">{{ errorModalMessage }}</p>
          <ul
            v-if="errorModalItems.length"
            class="list-unstyled mb-0 carteira-cadastros-vue__error-list"
          >
            <li v-for="(row, i) in errorModalItems" :key="i" class="small">
              <template v-if="row.label">
                <strong>{{ row.label }}:</strong>
                {{ row.text }}
              </template>
              <template v-else>{{ row.text }}</template>
            </li>
          </ul>
        </template>
        <template #footer>
          <button type="button" class="btn btn-primary btn-sm" @click="closeErrorModal">Fechar</button>
        </template>
      </Modal>

      <Modal
        modal-id="modalCarteiraCadastroCamposExcluir"
        title="Excluir registro"
        dialog-class="modal-dialog-centered"
      >
        <template #body>
          <p v-if="deleteTarget" class="mb-0">{{ deleteConfirmText }}</p>
        </template>
        <template #footer>
          <Button variant="danger" size="sm" :loading="deleteDeleting" @click="executeDeleteConfirmed">
            {{ deleteDeleting ? "Excluindo…" : "Excluir" }}
          </Button>
          <button
            type="button"
            class="btn btn-secondary btn-sm ml-2"
            :disabled="deleteDeleting"
            data-dismiss="modal"
            @click="closeDeleteConfirmModal"
          >
            Cancelar
          </button>
        </template>
      </Modal>

      <Modal modal-id="modalCarteiraCadastroCamposItem" :title="modalTitle" dialog-class="modal-lg">
        <template #body>
          <Input
            v-model="form.nome"
            name="nome"
            id="carteira-cadastro-campo-nome"
            label="Nome"
            placeholder="Nome"
            required
          />
          <div class="mb-3">
            <label class="form-label d-block">Tipo</label>
            <select v-model.number="form.tipo" class="form-control form-control-sm">
              <option v-for="opt in tipoOpcoes" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div v-if="form.tipo === TIPO_LISTA" class="mb-3">
            <label class="form-label d-block">Opções da lista</label>
            <div v-for="(opcao, idx) in form.conteudo" :key="idx" class="d-flex mb-2">
              <input
                v-model="form.conteudo[idx]"
                type="text"
                class="form-control form-control-sm mr-2"
                placeholder="Opção"
              />
              <button type="button" class="btn btn-outline-danger btn-sm" @click="removeOpcao(idx)">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="addOpcao">
              <i class="fas fa-plus mr-1"></i> Adicionar opção
            </button>
          </div>
          <Input
            v-model="ordemStr"
            name="ordem"
            id="carteira-cadastro-campo-ordem"
            label="Ordem"
            type="number"
            placeholder="Sequência de exibição"
          />
          <div class="mb-2">
            <Check v-model="form.ativo">Ativo</Check>
          </div>
          <div class="mb-2">
            <Check v-model="form.exibir_no_kanban">Exibir nos cards do Kanban</Check>
          </div>
          <div class="form-group mb-3">
            <label class="small mb-1 d-block" for="carteira-cadastro-campo-cor-kanban">Cor do texto no card</label>
            <div class="d-flex align-items-stretch flex-wrap carteira-cadastros-vue__cor-inputs">
              <input
                id="carteira-cadastro-campo-cor-kanban"
                v-model="form.cor_kanban"
                type="color"
                class="form-control border carteira-cadastros-vue__cor-swatch p-1"
                aria-label="Escolher cor do texto no card"
              />
              <input
                v-model="form.cor_kanban"
                type="text"
                class="form-control form-control-sm carteira-cadastros-vue__cor-hex"
                maxlength="7"
                placeholder="#198754"
                autocapitalize="off"
                autocomplete="off"
                spellcheck="false"
              />
            </div>
            <small v-if="!form.exibir_no_kanban" class="text-muted">
              Usada quando o campo estiver marcado para exibir nos cards.
            </small>
          </div>
          <div class="mb-3" v-if="form.tipo === TIPO_NUMERICO">
            <Check v-model="form.somar_no_kanban">
              Somar no header da coluna (apenas numérico)
            </Check>
          </div>
          <p v-if="formError" class="text-danger small mb-0">{{ formError }}</p>
        </template>
        <template #footer>
          <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal">Cancelar</button>
          <Button variant="primary" size="sm" :loading="saving" @click="saveModal">Salvar</Button>
        </template>
      </Modal>
    </div>

    <div v-if="!localUsuarioPodeCadastro" class="alert alert-warning">
      Somente administradores ou gerentes podem gerenciar estes cadastros.
    </div>

    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap carteira-cadastros-vue__toolbar">
      <h5 class="mb-0"></h5>
      <Button
        v-if="localUsuarioPodeCadastro"
        variant="success"
        size="sm"
        depth
        @click="openEditModal(null)"
      >
        <Icon type="fas" icon="plus" color="white" class="mr-1" />
        Adicionar
      </Button>
    </div>

    <div v-if="loading" class="text-center py-4">Carregando...</div>
    <div v-else class="carteira-cadastros-vue__card carteira-cadastros-vue__datatable-wrap">
      <DataTable
        name="CamposPersonalizados"
        :headers="headers"
        :data="items"
        :pesquisa="true"
        :options="tableOptions"
        :itens-per-page-options="[10, 20, 30, 50, 100, -1]"
      >
        <template v-slot:[`item.tipo`]="{ item }">
          {{ tipoLabel(item.tipo) }}
        </template>
        <template v-slot:[`item.exibir_no_kanban`]="{ item }">
          {{ isTruthy(item.exibir_no_kanban) ? "Sim" : "Não" }}
        </template>
        <template v-slot:[`item.cor_kanban`]="{ item }">
          <span
            class="carteira-cadastros-vue__cor-preview"
            :style="{ backgroundColor: corKanbanPreview(item) }"
            :title="corKanbanPreview(item)"
          ></span>
        </template>
        <template v-slot:[`item.somar_no_kanban`]="{ item }">
          {{ isTruthy(item.somar_no_kanban) ? "Sim" : "Não" }}
        </template>
        <template v-slot:[`item.ativo`]="{ item }">
          <span
            class="campanha-interna-vue-status-pill"
            :class="
              isAtivo(item)
                ? 'campanha-interna-vue-status-pill--ativa'
                : 'campanha-interna-vue-status-pill--inativa'
            "
          >
            {{ isAtivo(item) ? "Ativo" : "Inativo" }}
          </span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-content-end align-items-center flex-wrap carteira-cadastros-vue__row-actions">
            <Button
              v-if="localUsuarioPodeCadastro"
              variant="primary"
              size="sm"
              table
              class="mr-1 mb-1"
              title="Editar"
              @click="openEditModal(item)"
            >
              <i class="fas fa-edit"></i>
            </Button>
            <Button
              v-if="localUsuarioPodeCadastro"
              variant="danger"
              size="sm"
              table
              class="mb-1"
              title="Excluir"
              @click="openDeleteConfirmModal(item)"
            >
              <i class="fas fa-trash"></i>
            </Button>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import DataTable from "../../ui/DataTable.vue";
import Input from "../../ui/Input.vue";
import Check from "../../ui/Check.vue";
import Icon from "../../ui/Icon.vue";
import Button from "../../ui/Button.vue";
import Modal from "../../ui/Modal.vue";

const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest",
};

const TIPO_TEXTO = 1;
const TIPO_LISTA = 2;
const TIPO_NUMERICO = 3;
const TIPO_DATA = 4;
const COR_KANBAN_PADRAO = "#198754";

function normalizeHexCor(v) {
  if (v == null || v === "") return COR_KANBAN_PADRAO;
  const s = String(v).trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toLowerCase();
  if (/^[0-9A-Fa-f]{6}$/.test(s)) return `#${s}`.toLowerCase();
  return COR_KANBAN_PADRAO;
}

function fieldLabel(key) {
  const map = {
    nome: "Nome",
    tipo: "Tipo",
    ordem: "Ordem",
    ativo: "Ativo",
    exibir_no_kanban: "Exibir no Kanban",
    somar_no_kanban: "Somar no Kanban",
    cor_kanban: "Cor no card",
    conteudo: "Opções",
  };
  return map[key] || String(key).replace(/_/g, " ");
}

function bs$(selector) {
  const jq = typeof window !== "undefined" ? window.jQuery || window.$ : null;
  if (!jq) {
    return { modal: () => {} };
  }
  return jq(selector);
}

export default {
  name: "CarteiraCadastroCamposPersonalizadosApp",
  components: { DataTable, Input, Check, Icon, Button, Modal },
  data() {
    return {
      TIPO_LISTA,
      TIPO_NUMERICO,
      localApiUrls: {},
      localUsuarioPodeCadastro: false,
      items: [],
      loading: false,
      saving: false,
      tableOptions: { page: 1, itemsPerPage: 30 },
      editingId: null,
      form: {
        nome: "",
        tipo: TIPO_TEXTO,
        conteudo: [],
        ordem: null,
        ativo: true,
        exibir_no_kanban: false,
        somar_no_kanban: false,
        cor_kanban: COR_KANBAN_PADRAO,
      },
      formError: "",
      errorModalTitle: "Atenção",
      errorModalMessage: "",
      errorModalItems: [],
      deleteTarget: null,
      deleteDeleting: false,
      tipoOpcoes: [
        { value: TIPO_TEXTO, label: "Texto" },
        { value: TIPO_LISTA, label: "Lista" },
        { value: TIPO_NUMERICO, label: "Numérico" },
        { value: TIPO_DATA, label: "Data" },
      ],
      headers: [
        { text: "#", value: "id", sortable: true, width: "72px" },
        { text: "Nome", value: "nome", sortable: true },
        { text: "Tipo", value: "tipo", sortable: true },
        { text: "Ordem", value: "ordem", sortable: true },
        { text: "Card Kanban", value: "exibir_no_kanban", sortable: true },
        { text: "Cor card", value: "cor_kanban", sortable: false, width: "88px" },
        { text: "Soma coluna", value: "somar_no_kanban", sortable: true },
        { text: "Ativo", value: "ativo", sortable: true },
        { text: "Ações", value: "actions", sortable: false, align: "end", width: "120px" },
      ],
    };
  },
  computed: {
    modalTitle() {
      return `${this.editingId ? "Editar" : "Novo"} campo personalizado`;
    },
    ordemStr: {
      get() {
        const o = this.form.ordem;
        if (o === null || o === undefined || o === "") return "";
        return String(o);
      },
      set(v) {
        if (v === "" || v === null || v === undefined) {
          this.form.ordem = null;
          return;
        }
        const n = Number(v);
        this.form.ordem = Number.isNaN(n) ? null : n;
      },
    },
    deleteConfirmText() {
      if (!this.deleteTarget) return "";
      const nome = this.deleteTarget.nome || `#${this.deleteTarget.id}`;
      return `Excluir o campo "${nome}"? Esta ação não pode ser desfeita.`;
    },
  },
  watch: {
    "form.tipo"(val) {
      if (Number(val) !== TIPO_NUMERICO) {
        this.form.somar_no_kanban = false;
      }
      if (Number(val) !== TIPO_LISTA && (!this.form.conteudo || !this.form.conteudo.length)) {
        this.form.conteudo = [];
      }
    },
    "form.somar_no_kanban"(val) {
      if (val) {
        this.form.exibir_no_kanban = true;
      }
    },
  },
  mounted() {
    const cfg = typeof window !== "undefined" ? window.carteiraCadastroConfig || {} : {};
    this.localApiUrls = cfg.apiUrls && Object.keys(cfg.apiUrls).length ? cfg.apiUrls : {};
    axios.defaults.headers.common["X-CSRF-TOKEN"] = cfg.csrfToken || "";
    axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
    this.localUsuarioPodeCadastro =
      cfg.usuarioPodeCadastro !== undefined ? !!cfg.usuarioPodeCadastro : false;
    this.loadList();
  },
  methods: {
    tipoLabel(tipo) {
      const n = Number(tipo);
      const found = this.tipoOpcoes.find((o) => o.value === n);
      return found ? found.label : String(tipo);
    },
    corKanbanPreview(item) {
      return normalizeHexCor(item && item.cor_kanban);
    },
    isTruthy(v) {
      return v === true || v === 1 || v === "1";
    },
    isAtivo(item) {
      const s = item && item.ativo;
      if (s === true || s === 1 || s === "1") return true;
      if (s === false || s === 0 || s === "0") return false;
      return !!s;
    },
    addOpcao() {
      this.form.conteudo = [...(this.form.conteudo || []), ""];
    },
    removeOpcao(idx) {
      this.form.conteudo = (this.form.conteudo || []).filter((_, i) => i !== idx);
    },
    showErrorModal({ title, message, errors }) {
      this.errorModalTitle = title || "Atenção";
      this.errorModalMessage = message || "";
      this.errorModalItems = [];
      if (errors && typeof errors === "object" && !Array.isArray(errors)) {
        Object.keys(errors).forEach((key) => {
          const msgs = errors[key];
          const arr = Array.isArray(msgs) ? msgs : [msgs];
          const label = fieldLabel(key);
          arr.forEach((m) => {
            if (m != null && String(m).trim() !== "") {
              this.errorModalItems.push({ label, text: String(m) });
            }
          });
        });
      }
      if (!this.errorModalMessage && this.errorModalItems.length) {
        this.errorModalMessage = "Corrija os campos indicados abaixo.";
      }
      this.$nextTick(() => bs$("#modalCarteiraCadastroCamposErro").modal("show"));
    },
    closeErrorModal() {
      bs$("#modalCarteiraCadastroCamposErro").modal("hide");
    },
    openDeleteConfirmModal(item) {
      this.deleteTarget = item;
      this.deleteDeleting = false;
      this.$nextTick(() => bs$("#modalCarteiraCadastroCamposExcluir").modal("show"));
    },
    closeDeleteConfirmModal() {
      bs$("#modalCarteiraCadastroCamposExcluir").modal("hide");
      this.deleteTarget = null;
    },
    async loadList() {
      this.loading = true;
      try {
        const { data } = await axios.get(this.localApiUrls.camposPersonalizados, {
          headers: JSON_HEADERS,
        });
        this.items = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error(e);
        this.items = [];
        this.showErrorModal({ title: "Lista", message: "Não foi possível carregar os dados." });
      } finally {
        this.loading = false;
      }
    },
    openEditModal(row) {
      this.formError = "";
      this.editingId = row ? row.id : null;
      this.form = {
        nome: row ? row.nome : "",
        tipo: row ? Number(row.tipo) : TIPO_TEXTO,
        conteudo: row && Array.isArray(row.conteudo) ? [...row.conteudo] : [],
        ordem: row ? row.ordem : null,
        ativo: row ? this.isAtivo(row) : true,
        exibir_no_kanban: row ? this.isTruthy(row.exibir_no_kanban) : false,
        somar_no_kanban: row ? this.isTruthy(row.somar_no_kanban) : false,
        cor_kanban: row ? normalizeHexCor(row.cor_kanban) : COR_KANBAN_PADRAO,
      };
      this.$nextTick(() => bs$("#modalCarteiraCadastroCamposItem").modal("show"));
    },
    async saveModal() {
      this.formError = "";
      this.saving = true;
      try {
        const payload = {
          nome: this.form.nome,
          tipo: Number(this.form.tipo),
          conteudo: Number(this.form.tipo) === TIPO_LISTA ? this.form.conteudo : null,
          ordem:
            this.form.ordem === null || this.form.ordem === "" ? null : Number(this.form.ordem),
          ativo: !!this.form.ativo,
          exibir_no_kanban: !!this.form.exibir_no_kanban,
          somar_no_kanban: !!this.form.somar_no_kanban,
          cor_kanban: normalizeHexCor(this.form.cor_kanban),
        };
        const base = this.localApiUrls.campoPersonalizadoBase;
        if (this.editingId) {
          await axios.put(`${base}/${this.editingId}`, payload, { headers: JSON_HEADERS });
        } else {
          await axios.post(base, payload, { headers: JSON_HEADERS });
        }
        bs$("#modalCarteiraCadastroCamposItem").modal("hide");
        await this.loadList();
      } catch (e) {
        const d = e.response && e.response.data ? e.response.data : {};
        if (e.response && e.response.status === 422) {
          this.showErrorModal({
            title: "Dados inválidos",
            message: d.message || "Os dados enviados são inválidos.",
            errors: d.errors,
          });
        } else {
          const msg =
            (typeof d.message === "string" && d.message) ||
            (typeof d.error === "string" && d.error) ||
            "Não foi possível salvar.";
          this.formError = typeof msg === "string" ? msg : JSON.stringify(msg);
        }
      } finally {
        this.saving = false;
      }
    },
    async executeDeleteConfirmed() {
      if (!this.deleteTarget) {
        this.closeDeleteConfirmModal();
        return;
      }
      const id = this.deleteTarget.id;
      this.deleteDeleting = true;
      try {
        await axios.delete(`${this.localApiUrls.campoPersonalizadoBase}/${id}`, {
          headers: JSON_HEADERS,
        });
        this.closeDeleteConfirmModal();
        await this.loadList();
      } catch (e) {
        this.closeDeleteConfirmModal();
        const d = e.response && e.response.data ? e.response.data : {};
        this.showErrorModal({
          title: "Exclusão",
          message:
            (typeof d.message === "string" && d.message) || "Não foi possível excluir este registro.",
        });
      } finally {
        this.deleteDeleting = false;
      }
    },
  },
};
</script>

<style scoped>
.carteira-cadastros-vue__toolbar {
  gap: 0.5rem;
}

.carteira-cadastros-vue__error-list li + li {
  margin-top: 0.35rem;
}

.carteira-cadastros-vue__datatable-wrap ::v-deep .container-fluid {
  padding-left: 0;
  padding-right: 0;
}

.carteira-cadastros-vue__cor-inputs {
  gap: 0.5rem;
}

.carteira-cadastros-vue__cor-swatch {
  width: 2.75rem;
  min-width: 2.75rem;
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.15rem;
}

.carteira-cadastros-vue__cor-hex {
  flex: 1 1 8rem;
  max-width: 8rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.carteira-cadastros-vue__cor-preview {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.2rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  vertical-align: middle;
}
</style>
